import { isWebSerialSupported } from '@spblock/core';

/**
 * Web Serial session layer: port acquisition with Espressif-relevant USB
 * filters, line-buffered reads, and writes with configurable line endings.
 * esptool-js owns the port during flashing; this session is for the monitor.
 */

export const SERIAL_BAUD_OPTIONS = [9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600];

/** USB VID/PID filters covering ESP32 native USB and common UART bridges. */
export const USB_PORT_FILTERS: SerialPortRequestOptions = {
  filters: [
    { usbVendorId: 0x303a }, // Espressif native USB (S2/S3/C3)
    { usbVendorId: 0x10c4, usbProductId: 0xea60 }, // SiLabs CP210x
    { usbVendorId: 0x0403, usbProductId: 0x6001 }, // FTDI FT232
    { usbVendorId: 0x0403, usbProductId: 0x6010 }, // FTDI FT2232
    { usbVendorId: 0x0403, usbProductId: 0x6014 }, // FTDI FT232H
    { usbVendorId: 0x0403, usbProductId: 0x6015 }, // FTDI FT-X
    { usbVendorId: 0x1a86, usbProductId: 0x7523 }, // WCH CH340
    { usbVendorId: 0x1a86, usbProductId: 0x55d4 }, // WCH CH9102
  ],
};

export type NewlineMode = '\n' | '\r' | '\r\n' | 'none';

export class SerialPortSession {
  private port: SerialPort | null = null;
  private keepReading = false;
  private decoder = new TextDecoder();
  private encoder = new TextEncoder();

  onLine?: (line: string) => void;
  onClose?: () => void;

  get isOpen(): boolean {
    return this.port !== null && this.keepReading;
  }

  get portInfo(): string {
    const info = this.port?.getInfo();
    if (!info?.usbVendorId) return 'native USB';
    const hex = (n: number) => n.toString(16).padStart(4, '0');
    return `USB ${hex(info.usbVendorId ?? 0)}:${hex(info.usbProductId ?? 0)}`;
  }

  async connect(baudRate: number): Promise<void> {
    if (!isWebSerialSupported()) {
      throw new Error('Web Serial is not available. Use Chrome or Edge (version 90 or newer).');
    }
    if (this.isOpen) return;

    const port = await navigator.serial.requestPort(USB_PORT_FILTERS);
    await port.open({ baudRate });
    this.port = port;
    this.keepReading = true;
    port.addEventListener('disconnect', () => this.handleDisconnect());
    void this.readLoop(port);
  }

  /** Attach the monitor to a port the flasher has already opened/used. */
  async attach(port: SerialPort, baudRate: number): Promise<void> {
    if (this.isOpen) await this.disconnect();
    try {
      await port.open({ baudRate });
    } catch (err) {
      // InvalidStateError means the port is already open — reuse it as-is.
      if (!(err instanceof Error && err.name === 'InvalidStateError')) throw err;
    }
    this.port = port;
    this.keepReading = true;
    port.addEventListener('disconnect', () => this.handleDisconnect());
    void this.readLoop(port);
  }

  private async readLoop(port: SerialPort): Promise<void> {
    let buffer = '';
    while (port.readable && this.keepReading) {
      const reader = port.readable.getReader();
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += this.decoder.decode(value, { stream: true });
          const lines = buffer.split(/\r\n|\n|\r/);
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (line.length > 0) this.onLine?.(line);
          }
        }
      } catch {
        break;
      } finally {
        reader.releaseLock();
      }
    }
    if (buffer.length > 0) this.onLine?.(buffer);
  }

  async writeLine(text: string, mode: NewlineMode = '\n'): Promise<void> {
    if (!this.port?.writable) throw new Error('The serial port is not open.');
    const suffix = mode === 'none' ? '' : mode;
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(this.encoder.encode(`${text}${suffix}`));
    } finally {
      writer.releaseLock();
    }
  }

  private handleDisconnect(): void {
    void this.disconnect();
  }

  async disconnect(): Promise<void> {
    this.keepReading = false;
    const port = this.port;
    this.port = null;
    if (port) {
      try {
        await port.close();
      } catch {
        /* already closed by the OS */
      }
    }
    this.onClose?.();
  }
}

/* ------------------------------------------------------------------ */
/* Monitor singleton                                                   */
/* ------------------------------------------------------------------ */

let monitorSession: SerialPortSession | null = null;

export function getSerialSession(): SerialPortSession | null {
  return monitorSession;
}

export async function startSerialMonitor(
  baudRate: number,
  onLine: (line: string) => void,
): Promise<SerialPortSession> {
  if (!monitorSession) {
    monitorSession = new SerialPortSession();
  }
  monitorSession.onLine = onLine;
  if (!monitorSession.isOpen) {
    await monitorSession.connect(baudRate);
  }
  return monitorSession;
}

export async function monitorExistingPort(
  port: SerialPort,
  baudRate: number,
  onLine: (line: string) => void,
): Promise<SerialPortSession> {
  if (!monitorSession) {
    monitorSession = new SerialPortSession();
  }
  monitorSession.onLine = onLine;
  await monitorSession.attach(port, baudRate);
  return monitorSession;
}

export async function stopSerialMonitor(): Promise<void> {
  if (monitorSession) {
    await monitorSession.disconnect();
    monitorSession = null;
  }
}
